import React from "react";
import PropTypes from "prop-types";
import { ShieldCheck, ShieldAlert, ShieldQuestion } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const PHONE_TYPE_LABELS = {
  mobile: "Móvil",
  landline: "Fijo",
  voip: "VoIP",
};

/**
 * Derives a display status from a lead's `phone_validation` object.
 * - null validation -> "Sin validar" (no provider data)
 * - mobile          -> reachable via WhatsApp/SMS
 * - anything else   -> valid line but WhatsApp likely won't work
 */
export function getPhoneValidationStatus(validation) {
  if (!validation) {
    return { tone: "muted", label: "Sin validar", Icon: ShieldQuestion, isMobile: false };
  }
  const isMobile = validation.phone_type === "mobile";
  return {
    tone: isMobile ? "ok" : "warn",
    label: PHONE_TYPE_LABELS[validation.phone_type] || validation.phone_type || "Validado",
    Icon: isMobile ? ShieldCheck : ShieldAlert,
    isMobile,
  };
}

/**
 * Best number for wa.me links — digits only, no leading "+" (wa.me requires this).
 * Prefers the E.164-normalized cleansed_phone when available.
 */
export function getWhatsappPhone(validation, fallbackPhone) {
  return (validation?.cleansed_phone || fallbackPhone || "").replace(/\D/g, "");
}

/**
 * Best number for tel: links — keeps the leading "+" so mobile dialers retain
 * international context. cleansed_phone is already E.164 (e.g. "+523316797455");
 * the raw fallback is stripped to digits/"+" only.
 */
export function getTelPhone(validation, fallbackPhone) {
  if (validation?.cleansed_phone) return validation.cleansed_phone;
  return (fallbackPhone || "").replace(/[^\d+]/g, "");
}

const TONE_CLASSES = {
  ok: "bg-green-100 text-green-700 border-green-200",
  warn: "bg-amber-100 text-amber-700 border-amber-200",
  muted: "bg-gray-100 text-gray-500 border-gray-200",
};

/**
 * Visual indicator for a lead's phone validation data.
 * `compact` renders an icon-only chip for dense list/table rows.
 */
export default function PhoneValidationBadge({ validation, compact = false }) {
  const { tone, label, Icon } = getPhoneValidationStatus(validation);

  const badge = (
    <Badge
      variant="outline"
      className={`gap-1 font-medium ${TONE_CLASSES[tone]}`}
      onClick={(e) => e.stopPropagation()}
    >
      <Icon className="h-3.5 w-3.5" />
      {!compact && label}
    </Badge>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent className="space-y-0.5">
          {validation ? (
            <>
              <p className="font-semibold">{label}</p>
              {validation.carrier && <p>Operador: {validation.carrier}</p>}
              {(validation.city || validation.country) && (
                <p>{[validation.city, validation.country].filter(Boolean).join(", ")}</p>
              )}
            </>
          ) : (
            <p>Sin datos de validación para este número</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

PhoneValidationBadge.propTypes = {
  validation: PropTypes.shape({
    provider: PropTypes.string,
    cleansed_phone: PropTypes.string,
    national_phone: PropTypes.string,
    carrier: PropTypes.string,
    phone_type: PropTypes.string,
    country: PropTypes.string,
    country_code: PropTypes.string,
    country_code_numeric: PropTypes.string,
    city: PropTypes.string,
  }),
  compact: PropTypes.bool,
};
